import React, { useState, useEffect, useRef } from 'react'
import { 
  CheckCircle,
  XCircle,
  Clock,
  UserCheck,
  ClipboardList,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import timesheetApprovalService from '../../services/timesheetApprovalService'
// Consolidated CSS is imported globally

const TimesheetApproval = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalApprovals: 0,
    pendingApprovals: 0,
    approvedApprovals: 0,
    rejectedApprovals: 0,
    partiallyApprovedApprovals: 0,
    returnedApprovals: 0,
    totalHoursPending: 0,
    totalBillingAmountPending: 0
  })
  const [filterStatus, setFilterStatus] = useState('Pending')
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [filterStatus])

  const loadData = async () => {
    try {
      setLoading(true)
      const [approvalsResponse, statsResponse] = await Promise.all([
        timesheetApprovalService.getAllTimesheetApprovals({ status: filterStatus }),
        timesheetApprovalService.getTimesheetApprovalStats()
      ])
      
      if (approvalsResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (approvalsResponse.data || []).map(approval => ({
          ...approval,
          id: approval._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (approvalsResponse.data?.length === 0 && !hasShownEmptyToastRef.current && filterStatus === 'Pending') {
          hasShownEmptyToastRef.current = true
          toast.info('No pending timesheet approvals found.')
        }
      } else {
        toast.error(approvalsResponse.message || 'Failed to load timesheet approvals')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading timesheet approval records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view timesheet approvals')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load timesheet approval records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatHours = (hours) => {
    if (!hours) return '0.0h'
    return `${parseFloat(hours).toFixed(1)}h`
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const formatWeekRange = (weekStartDate, weekEndDate) => {
    if (!weekStartDate || !weekEndDate) return '-'
    const start = new Date(weekStartDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const end = new Date(weekEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return `${start} - ${end}`
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Pending': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Partially Approved': 'warning',
      'Returned': 'inactive'
    }
    return statusMap[status] || 'inactive'
  }

  const getPriorityBadgeClass = (priority) => {
    const priorityMap = {
      'Low': 'inactive',
      'Medium': 'pending',
      'High': 'warning',
      'Urgent': 'danger'
    }
    return priorityMap[priority] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'approvalNumber',
      header: 'Approval Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'timesheetNumber',
      header: 'Timesheet Number',
      cell: ({ getValue, row }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue() || row.original.weeklyTimesheet?.timesheetNumber || '-'}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'weekStartDate',
      header: 'Week',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'center' }}>
          {formatWeekRange(getValue(), row.original.weekEndDate)}
          {row.original.weekNumber && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>Week {row.original.weekNumber}, {row.original.year}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'totalHours',
      header: 'Total Hours',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {formatHours(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'overtimeHours',
      header: 'Overtime',
      cell: ({ getValue }) => {
        const hours = parseFloat(getValue() || 0)
        return (
          <div style={{ textAlign: 'center', color: hours > 0 ? '#dc3545' : '#6b7280' }}>
            {formatHours(hours)}
          </div>
        )
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getPriorityBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const approval = row.original
        if (approval.status !== 'Pending') {
          return (
            <div style={{ textAlign: 'center' }}>
              <button
                onClick={() => handleView(approval)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '4px',
                  background: '#fff',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontFamily: 'Inter, sans-serif',
                  color: '#495057'
                }}
              >
                View
              </button>
            </div>
          )
        }
        return (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            <button
              onClick={() => handleView(approval)}
              style={{
                padding: '6px 12px',
                border: '1px solid #e9ecef',
                borderRadius: '4px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '13px',
                fontFamily: 'Inter, sans-serif',
                color: '#495057'
              }}
            >
              View
            </button>
            <button
              onClick={() => handleApprove(approval)}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '4px',
                background: '#28a745',
                cursor: 'pointer',
                fontSize: '13px',
                fontFamily: 'Inter, sans-serif',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <CheckCircle size={14} />
              Approve
            </button>
          </div>
        )
      },
    },
  ]

  const handleView = (approvalData) => {
    navigate('/timesheets/timesheet-approval/view', { state: { approvalData } })
  }

  const handleApprove = (approvalData) => {
    navigate('/timesheets/timesheet-approval/approve', { state: { approvalData } })
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Timesheets', path: '/timesheets' },
          { label: 'Approvals', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Timesheet Approvals</h1>
          <p>Timesheet approval and rejection workflows</p>
        </div>
        <div className="page-actions">
          <select
            className="form-select form-select-sm"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
            <option value="Partially Approved">Partially Approved</option>
            <option value="Returned">Returned</option>
            <option value="">All</option>
          </select>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Pending</span>
            </div>
          </div>
          <div className="metric-value">{stats.pendingApprovals || 0}</div>
          <div className="metric-title">Pending Approvals</div>
          <div className="metric-subtitle">Awaiting action</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedApprovals || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Approved timesheets</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <span>Rejected</span>
            </div>
          </div>
          <div className="metric-value">{stats.rejectedApprovals || 0}</div>
          <div className="metric-title">Rejected</div>
          <div className="metric-subtitle">Rejected timesheets</div>
        </div>

        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <UserCheck size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Hours</span>
            </div>
          </div>
          <div className="metric-value">{formatHours(stats.totalHoursPending)}</div>
          <div className="metric-title">Pending Hours</div>
          <div className="metric-subtitle">Hours awaiting approval</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Amount</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalBillingAmountPending)}</div>
          <div className="metric-title">Pending Amount</div>
          <div className="metric-subtitle">Billing pending approval</div>
        </div>

        <div className="metric-card conversion">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalApprovals || 0}</div>
          <div className="metric-title">Total Approvals</div>
          <div className="metric-subtitle">All approval records</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>
    </div>
  )
}

export default TimesheetApproval

